import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Spinner } from '@ui/components/loaders'
import { ActionBar, SideBar } from '@ui/components/navigation'
import { PipelineNav } from '@ui/components/navigation/PipelineNav'
import axios from '@ui/lib/api/axiosHelper'
import { useAuth } from '@ui/providers/AuthProvider'
import {
  FeatureFlagName,
  useGetLocalOverrideFeatureFlag,
  useSetLocalOverrideFeatureFlag,
} from '@ui/providers/FeatureFlagProvider'
import { useInstanceStore, useNodeAndEdgeStore, usePipelinesStore } from '@ui/stores'
import clsx from 'clsx'

import { InstanceNotificationWrapper } from './InstanceNotificationWrapper'

export function NavigationLayout() {
  const getLocalFeatureFlag = useGetLocalOverrideFeatureFlag()
  const setLocalFeatureFlag = useSetLocalOverrideFeatureFlag()

  const location = useLocation()
  const { currentOrganization } = useAuth()

  const { resetStore: resetPipelinesStore } = usePipelinesStore(
    useShallow((state) => ({
      resetStore: state.resetStore,
    }))
  )

  const { switchWorkflowUpdate } = useNodeAndEdgeStore(
    useShallow((state) => ({
      switchWorkflowUpdate: state.switchWorkflowUpdate,
    }))
  )

  const { selectedInstanceKey, instances, setInstances } = useInstanceStore(
    useShallow((state) => ({
      selectedInstanceKey: state.selectedInstanceKey,
      instances: state.instances,
      setInstances: state.setInstances,
      resetStore: state.resetStore,
    }))
  )

  const [loading, setLoading] = useState(false)

  const fetchInstances = useCallback(async () => {
    const response = await axios.get(`/v1/organizations/${currentOrganization?.id}/instances`)
    const instances = response.data
    setInstances(instances)
  }, [currentOrganization, setInstances])

  const setWorkflowUpdateEnabled = useCallback(() => {
    setLocalFeatureFlag(FeatureFlagName.WorkflowUpdate, switchWorkflowUpdate)
  }, [switchWorkflowUpdate, setLocalFeatureFlag])

  useEffect(() => {
    setWorkflowUpdateEnabled()
  }, [setWorkflowUpdateEnabled])

  const workflowUpdateEnabled = useMemo(() => {
    return getLocalFeatureFlag(FeatureFlagName.WorkflowUpdate)
  }, [getLocalFeatureFlag])

  useEffect(() => {
    // We want to check if the user has any instances setup within this org.
    if (!instances.length) {
      fetchInstances()
    }

    // If the user has an instances and one is selected, we want to check if it's active.
    // This way when a user switches instances we refresh that context within the application.
    if (selectedInstanceKey && instances.length) {
      setLoading(true)
      const timer = setTimeout(() => {
        setLoading(false)
      }, 1000)
      return () => clearTimeout(timer)
    }

    // We're checking if a user is switching organizations.
    // This should cause a rerender that cascades starting with fetching the instances of this new organization
    if (currentOrganization) {
      resetPipelinesStore()
      setLoading(true)
      const timer = setTimeout(() => {
        setLoading(false)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [selectedInstanceKey, currentOrganization, instances, fetchInstances, switchWorkflowUpdate, resetPipelinesStore])

  const handleLayout = useCallback(() => {
    if (loading) {
      return (
        <div className="border-primary size-full flex items-center">
          <Spinner />
        </div>
      )
    }

    return <InstanceNotificationWrapper />
  }, [loading])

  const isPipelinePath = useMemo(() => {
    return /pipelines\/(new|\w+)/.test(location.pathname)
  }, [location.pathname])

  const showSideBar = useMemo(() => {
    return !workflowUpdateEnabled || !isPipelinePath
  }, [workflowUpdateEnabled, isPipelinePath])

  const navComponent = useMemo(() => {
    if (!workflowUpdateEnabled) return <ActionBar />
    return isPipelinePath ? <PipelineNav /> : <ActionBar />
  }, [workflowUpdateEnabled, isPipelinePath])

  return (
    <main className="flex h-screen w-screen flex-row">
      {showSideBar && <SideBar />}

      <div className="relative flex max-h-full w-full max-w-full flex-col">
        {navComponent}
        <div className={clsx('size-full overflow-hidden bg-gray-100', isPipelinePath ? 'p-0' : 'p-2.5')}>
          {handleLayout()}
        </div>
      </div>
    </main>
  )
}
